/**
 * resolveExecutionResponsibilities — EN-2 (canonical accountability seam)
 * ──────────────────────────────────────────────────────────────────────
 * The SINGLE legal read path for execution responsibility on a job.
 *
 * After EN-2, production code MUST NOT read `Job.assignedSubcontractorId`
 * directly to determine:
 *   • who performed the work
 *   • who is accountable
 *   • who gets paid
 *   • who owns warranty
 *   • who dispatch should target
 *   • who the scorecard/score is computed against
 *
 * Allowed direct reads of `assignedSubcontractorId` remain:
 *   • the writer (`assignContractor()`)
 *   • assignment/locking checks (e.g. "is this job already assigned?")
 *   • display-only ("show me jobs assigned to X" admin grids)
 *   • route params / audit snapshots
 *   • the resolver implementation itself
 *
 * ── Behavior in EN-2 ──
 * ZERO behavior change. All six roles resolve to `assignedSubcontractorId`
 * exactly. The four new EN-1 fields (`entityType`, `parentEntityId`,
 * `relationshipType`, `accountabilityMode`) are intentionally NOT branched
 * on yet. That activation lands in EN-3+ when a real licensee model
 * demands parent/crew/franchise routing.
 *
 * Null safety: any job without an assigned subcontractor returns nulls
 * across the board. Resolver never throws on beta/demo data.
 *
 * Determinism: pure function. Same (job, contractors) → same output.
 *
 * Anchor: mem://architecture/network-execution-phase-1a
 *         RECONCILIATION.md §9 (EN-0 lock), §10 (EN-1), §11 (EN-2)
 */

import type { Contractor, Job } from "@/data/types";

export interface ExecutionResponsibilities {
  /** Entity that physically performed the work on-site. */
  performedByEntityId: string | null;
  /** Entity ultimately accountable for outcomes (callbacks, QA, disputes). */
  accountableEntityId: string | null;
  /** Entity that receives payout for the job. */
  paidEntityId: string | null;
  /** Entity that owns warranty obligations. */
  warrantyEntityId: string | null;
  /** Entity that dispatch surfaces / messages route to. */
  dispatchTargetEntityId: string | null;
  /** Entity whose execution scorecard this job rolls into. */
  scoreSubjectEntityId: string | null;
}

const NULL_RESPONSIBILITIES: ExecutionResponsibilities = {
  performedByEntityId: null,
  accountableEntityId: null,
  paidEntityId: null,
  warrantyEntityId: null,
  dispatchTargetEntityId: null,
  scoreSubjectEntityId: null,
};

/**
 * Canonical resolver. Behavior in EN-2 is zero-change: every role maps
 * to `job.assignedSubcontractorId`. Future EN-3+ changes (parent routing,
 * crew→parent payout, certified→manufacturer warranty) MUST land here —
 * never inline at the call site.
 *
 * @param job          The job whose responsibilities to resolve.
 * @param _contractors Contractor directory. Unused in EN-2; reserved for
 *                     EN-3+ parent/crew/franchise traversal.
 */
export function resolveExecutionResponsibilities(
  job: Pick<Job, "assignedSubcontractorId">,
  _contractors: Contractor[] = [],
): ExecutionResponsibilities {
  const assigned = job?.assignedSubcontractorId ?? null;
  if (!assigned) return NULL_RESPONSIBILITIES;

  // EN-2 zero-change: every role collapses to the assigned subcontractor.
  // DO NOT branch on entityType / parentEntityId / accountabilityMode here
  // until EN-3 lands with explicit licensee-need justification.
  return {
    performedByEntityId: assigned,
    accountableEntityId: assigned,
    paidEntityId: assigned,
    warrantyEntityId: assigned,
    dispatchTargetEntityId: assigned,
    scoreSubjectEntityId: assigned,
  };
}
